import store_laboratoire from '@/store/mods/analyses/laboratoire'
import { sections_resultat, document_impression, date_heure } from '@/utils/compte-rendu-laboratoire'

// LAB-006 : compte rendu d'un bon d'analyses — lignes validées et diffusées seulement, mentions obligatoires
// (établissement via l'en-tête paramétré, patient, prescripteur, prélèvement, réception, validation, validateur,
// résultats avec unités et références, interprétation, version). Utilisé au laboratoire et dans le dossier médecin.
const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

export default {
    name: 'compte-rendu-laboratoire',
    components: {},
    props: {
        bulletin_id: { type: [Number, String], default: 0 },
        demande_id: { type: [Number, String], default: 0 },
        pdf_mode: { type: Boolean, default: false },
        toggler: { type: Number, default: 0 }
    },
    data() {
        return {
            modal: false, is_loading: false, erreur: '', rapport: null, requete: 0, format: 'A4', apercu: false, impression_prete: false, impression_en_cours: false,
            pdf_url: '', pdf_loading: false, pdf_error: '', pdf_sequence: 0,
            regrouper: false, selection_ids: []
        }
    },

    watch: {
        toggler() {
            this.ouvrir()
        },
        modal(ouvert) { if (!ouvert && this.pdf_mode) this.liberer_pdf() },
        html_impression() {
            this.impression_prete = false
            if (this.pdf_mode) this.liberer_pdf()
        }
    },

    computed: {
        analyses() {
            if (!this.rapport || !Array.isArray(this.rapport.analyses)) return []
            const visibles = this.regrouper
                ? this.rapport.analyses.filter(analyse => this.selection_ids.includes(Number(analyse.demande_id)))
                : (this.demande_id ? this.rapport.analyses.filter(analyse => Number(analyse.demande_id) === Number(this.demande_id)) : this.rapport.analyses)
            return visibles.map(analyse => Object.assign({}, analyse, { sections: sections_resultat(analyse.resultat) }))
        },

        // En-tête de l'établissement telle que paramétrée (mêmes sources que l'en-tête des bons et factures)
        entete() {
            const courant = this.$store && this.$store.getters && this.$store.getters['config_info_clinique']
            const info = (courant && typeof courant === 'object') ? courant : ((this.info_clinique && typeof this.info_clinique === 'object') ? this.info_clinique : {})
            const assets = typeof this.assets_path === 'string' ? this.assets_path : ''
            const image = (info.has_info && info.type_entete !== 'infos' && info.entete && assets) ? assets + '/config/' + info.entete : ''
            return {
                nom: info.nom || '', specialites: info.specialites || '', adresse: info.adresse || '', ville: info.ville || '', pays: info.pays || '',
                code_autorisation: info.code_autorisation || '', image: image, fuseau_horaire: info.fuseau_horaire || 'UTC'
            }
        },

        html_impression() {
            if (!this.rapport) return ''
            const rapport = this.pdf_mode && this.demande_id ? Object.assign({}, this.rapport, { disponibles: this.analyses.length, en_attente: 0 }) : this.rapport
            const titre_signature = (this.info_clinique && this.info_clinique.titre_signature_biologie) || 'Le Médecin biologiste'
            return document_impression(rapport, this.analyses, this.entete, this.format, this.regrouper, titre_signature)
        }
    },

    mounted() {

    },
    beforeDestroy() { this.liberer_pdf() },

    methods: {
        ouvrir() {
            this.modal = true
            this.apercu = false
            this.impression_prete = false
            this.regrouper = false
            this.selection_ids = []
            this.liberer_pdf()
            return this.charger()
        },
        liberer_pdf() {
            this.pdf_sequence += 1
            if (this.pdf_url) URL.revokeObjectURL(this.pdf_url)
            this.pdf_url = ''
            this.pdf_error = ''
            this.pdf_loading = false
        },
        changer_regroupement() {
            this.regrouper = !this.regrouper
            if (this.regrouper && this.rapport) this.selection_ids = this.rapport.analyses.map(analyse => Number(analyse.demande_id))
        },
        async preparer_pdf() {
            this.impression_prete = true
            if (!this.pdf_mode || !this.rapport || !this.analyses.length || !this.modal) return
            const cadre = this.$refs.impression
            const source = cadre && cadre.contentDocument && cadre.contentDocument.getElementById('pdf-source')
            if (!source) return
            const sequence = ++this.pdf_sequence
            this.pdf_loading = true
            this.pdf_error = ''
            try {
                const document_pdf = cadre.contentDocument
                if (document_pdf.fonts && document_pdf.fonts.ready) await document_pdf.fonts.ready
                await Promise.race([
                    Promise.all(Array.from(source.images || source.querySelectorAll('img')).map(image => image.complete ? Promise.resolve() : new Promise(resolve => {
                        image.onload = resolve
                        image.onerror = resolve
                    }))),
                    new Promise(resolve => setTimeout(resolve, 3000))
                ])
                if (sequence !== this.pdf_sequence || !this.modal) return
                const module = await import('html2pdf.js')
                const html2pdf = module.default || module
                // Les marges de la feuille d'aperçu ne doivent pas s'ajouter aux marges PDF.
                const contenu = source.cloneNode(true)
                contenu.classList.add('pdf-export')
                const blob = await html2pdf().set({
                    margin: 10, filename: 'resultat-' + (this.rapport.reference || this.bulletin_id) + '.pdf',
                    image: { type: 'jpeg', quality: 0.96 }, html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: 'mm', format: this.format.toLowerCase(), orientation: 'portrait' },
                    pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.patient-box', '.signature'] }
                }).from(contenu).outputPdf('blob')
                if (sequence !== this.pdf_sequence || !this.modal) return
                if (this.pdf_url) URL.revokeObjectURL(this.pdf_url)
                this.pdf_url = URL.createObjectURL(blob)
            } catch (_) {
                if (sequence === this.pdf_sequence) this.pdf_error = 'Le PDF n’a pas pu être préparé. Réessayez.'
            } finally { if (sequence === this.pdf_sequence) this.pdf_loading = false }
        },
        imprimer_pdf() {
            return this.imprimer()
        },

        charger() {
            const bulletin_id = Number(this.bulletin_id) || 0
            this.rapport = null
            this.erreur = ''
            if (!bulletin_id) {
                this.erreur = 'Bon d’analyse non renseigné.'
                return Promise.resolve()
            }
            this.is_loading = true
            const requete = ++this.requete
            return store_laboratoire.getCompteRendu(bulletin_id).then((response) => {
                if (requete !== this.requete) return
                if (!response || !response.success || !response.data) throw new Error('Réponse invalide')
                this.rapport = response.data
                this.selection_ids = this.demande_id ? [Number(this.demande_id)] : this.rapport.analyses.map(analyse => Number(analyse.demande_id))
                this.is_loading = false
            }).catch((error) => {
                if (requete !== this.requete) return
                this.is_loading = false
                this.erreur = message(error, 'Le compte rendu est indisponible pour le moment.')
            })
        },

        async imprimer() {
            const cadre = this.$refs.impression
            if (!cadre || !cadre.contentWindow || !this.impression_prete || this.impression_en_cours) return
            this.impression_en_cours = true
            try {
                const document = cadre.contentDocument
                if (document && document.fonts && document.fonts.ready) await document.fonts.ready
                if (document) {
                    await Promise.race([
                        Promise.all(Array.from(document.images).map(image => image.complete ? Promise.resolve() : new Promise(resolve => {
                            image.onload = resolve
                            image.onerror = resolve
                        }))),
                        new Promise(resolve => setTimeout(resolve, 3000))
                    ])
                }
                cadre.contentWindow.focus()
                cadre.contentWindow.print()
            } finally {
                this.impression_en_cours = false
            }
        },

        date_heure(value) {
            return date_heure(value, this.entete.fuseau_horaire)
        }
    }
}
